import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export default function AdminMedullaPanel() {
  const [revenue, setRevenue] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [filter, setFilter] = useState('pending_review');

  const adminKey = () => localStorage.getItem('nightbuzz_admin_key') || '';

  const load = async () => {
    try {
      const key = adminKey();
      const [revRes, campsRes] = await Promise.all([
        axios.get(`${API}/medulla/admin/revenue?admin_key=${encodeURIComponent(key)}`),
        axios.get(`${API}/medulla/admin/campaigns?admin_key=${encodeURIComponent(key)}${filter ? `&status=${filter}` : ''}`),
      ]);
      setRevenue(revRes.data);
      setCampaigns(campsRes.data);
    } catch (err) {
      toast.error('Failed to load Medulla data');
    }
  };

  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => {
    await axios.post(`${API}/medulla/admin/campaigns/${id}/approve?admin_key=${encodeURIComponent(adminKey())}`);
    toast.success('Approved');
    load();
  };

  const reject = async (id) => {
    const note = prompt('Rejection note (optional):') || '';
    await axios.post(`${API}/medulla/admin/campaigns/${id}/reject?admin_key=${encodeURIComponent(adminKey())}&note=${encodeURIComponent(note)}`);
    toast.success('Rejected');
    load();
  };

  return (
    <div className="space-y-4">
      {/* Revenue stats */}
      {revenue && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Revenue', v: `$${revenue.total_revenue}`, c: 'text-green-400' },
            { label: 'This Week', v: `$${revenue.week_revenue}`, c: 'text-yellow-400' },
            { label: 'Popup Bids', v: `$${revenue.popup_bid_revenue}`, c: 'text-cyan-400' },
            { label: 'QR Sales', v: `$${revenue.qr_marketplace_revenue}`, c: 'text-purple-400' },
            { label: 'Advertisers', v: revenue.total_advertisers, c: 'text-white' },
            { label: 'Total Campaigns', v: revenue.total_campaigns, c: 'text-white' },
            { label: 'Pending Review', v: revenue.pending_review, c: 'text-red-400' },
          ].map(s => (
            <Card key={s.label} className="p-3 bg-slate-900 border-slate-700">
              <p className="text-xs text-slate-400">{s.label}</p>
              <p className={`text-xl font-mono ${s.c}`}>{s.v}</p>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['pending_review', 'approved', 'rejected', ''].map(f => (
          <Button key={f || 'all'} onClick={() => setFilter(f)}
            size="sm"
            data-testid={`filter-${f || 'all'}`}
            className={filter === f ? 'bg-green-600' : 'bg-slate-800'}>
            {f || 'All'}
          </Button>
        ))}
      </div>

      {/* Campaign list */}
      <div className="space-y-2">
        {campaigns.length === 0 && <p className="text-slate-500 text-sm">No campaigns match.</p>}
        {campaigns.map(c => (
          <Card key={c.id} data-testid={`admin-campaign-${c.id}`} className="p-3 bg-slate-900 border-slate-700">
            <div className="flex items-start gap-3">
              {c.image_url && <img src={c.image_url} alt="" className="w-16 h-16 object-cover rounded" />}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h4 className="font-bold text-white">{c.title}</h4>
                  <span className="text-xs px-2 py-0.5 bg-slate-800 rounded">{c.type === 'popup_login' ? 'Popup' : 'QR'}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    c.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                    c.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>{c.status}</span>
                </div>
                <p className="text-xs text-slate-400">
                  by <b>{c.advertiser?.company_name}</b> ({c.advertiser?.email})
                </p>
                <p className="text-sm text-slate-300 mt-1">{c.description}</p>
                <a href={c.click_url} target="_blank" rel="noreferrer"
                  className="text-xs text-blue-400 underline">{c.click_url}</a>
              </div>
              {c.status === 'pending_review' && (
                <div className="flex flex-col gap-1">
                  <Button size="sm" onClick={() => approve(c.id)}
                    data-testid={`approve-${c.id}`}
                    className="bg-green-600 hover:bg-green-500">Approve</Button>
                  <Button size="sm" onClick={() => reject(c.id)}
                    data-testid={`reject-${c.id}`}
                    variant="outline" className="border-red-500 text-red-400">Reject</Button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
